import { useMemo } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import type { Service } from "@/lib/types";
import { ServiceTile } from "@/components/ServiceTile";
import { cn } from "@/lib/utils";
import { resolveGrid, type GridCell } from "@/lib/dashboardLayout";

// Fixed cols×rows grid where every tile occupies one explicit cell. Empty
// cells are real drop targets, so the layout can be sparse — tiles can mirror
// the physical spread of logger boxes. Services that don't fit fall into an
// overflow strip below and can be dragged back into a freed cell.

const CELL_ID = (col: number, row: number) => `cell:${col},${row}`;
const OVERFLOW_ID = "overflow-zone";

function parseCellId(id: string): GridCell | null {
  if (!id.startsWith("cell:")) return null;
  const [c, r] = id.slice(5).split(",").map(Number);
  if (!Number.isFinite(c) || !Number.isFinite(r)) return null;
  return { col: c, row: r };
}

function DraggableTile({
  svc,
  onClick,
}: {
  svc: Service;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: svc.system_id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 20 : undefined,
        cursor: isDragging ? "grabbing" : undefined,
      }}
      className="h-full"
    >
      <ServiceTile staticService={svc} onClick={onClick} />
    </div>
  );
}

function GridCellSlot({
  col,
  row,
  children,
}: {
  col: number;
  row: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: CELL_ID(col, row) });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "rounded-lg transition-colors",
        !children &&
          "border-2 border-dashed border-border min-h-[120px] flex items-center justify-center",
        isOver && "border-primary bg-primary/5",
      )}
    >
      {children ?? (
        <span className="text-[10px] text-muted-foreground/60 select-none">
          {col + 1},{row + 1}
        </span>
      )}
    </div>
  );
}

export function DashboardGridLayout({
  services,
  cols,
  rows,
  gridCells,
  onGridCellsChange,
  onOpen,
}: {
  services: Service[];
  cols: number;
  rows: number;
  gridCells: Record<string, GridCell>;
  onGridCellsChange: (next: Record<string, GridCell>) => void;
  onOpen: (systemId: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const ids = useMemo(() => services.map((s) => s.system_id), [services]);
  const byId = useMemo(
    () => new Map(services.map((s) => [s.system_id, s])),
    [services],
  );

  // Resolve every service to a concrete cell (or overflow) for this render.
  const { placed, overflow } = useMemo(
    () => resolveGrid({ gridCols: cols, gridRows: rows, gridCells }, ids),
    [cols, rows, gridCells, ids],
  );

  // Reverse map: "col,row" -> system_id, for cell rendering + occupancy checks.
  const occupant = useMemo(() => {
    const m = new Map<string, string>();
    for (const [sid, c] of Object.entries(placed)) m.set(`${c.col},${c.row}`, sid);
    return m;
  }, [placed]);

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over) return;
    const dragged = String(active.id);
    const overId = String(over.id);

    // Pin the whole current placement, then apply the one move — keeps every
    // other tile exactly where it sits regardless of auto-fill order.
    const next: Record<string, GridCell> = { ...placed };

    if (overId === OVERFLOW_ID) {
      delete next[dragged]; // push out to the overflow strip
      onGridCellsChange(next);
      return;
    }

    const target = parseCellId(overId);
    if (!target) return;
    const here = occupant.get(`${target.col},${target.row}`);
    if (here === dragged) return; // dropped on its own cell

    const fromCell = placed[dragged]; // undefined if dragged from overflow
    next[dragged] = { col: target.col, row: target.row };
    if (here) {
      // Swap: the displaced tile takes the dragged tile's old cell, or falls
      // to overflow if the dragged tile had none.
      if (fromCell) next[here] = fromCell;
      else delete next[here];
    }
    onGridCellsChange(next);
  }

  const cells: React.ReactNode[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const sid = occupant.get(`${col},${row}`);
      const svc = sid ? byId.get(sid) : undefined;
      cells.push(
        <GridCellSlot key={`${col},${row}`} col={col} row={row}>
          {svc ? (
            <DraggableTile svc={svc} onClick={() => onOpen(svc.system_id)} />
          ) : null}
        </GridCellSlot>,
      );
    }
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      {/* minmax(260px, 1fr): tiles stretch to fill the row when there's room,
          but never squish below 260px — the grid scrolls horizontally instead.
          Keeps high column counts readable on any monitor width. */}
      <div className="overflow-x-auto">
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(260px, 1fr))` }}
        >
          {cells}
        </div>
      </div>
      {overflow.length > 0 && (
        <OverflowStrip
          services={overflow.map((id) => byId.get(id)!).filter(Boolean)}
          onOpen={onOpen}
        />
      )}
    </DndContext>
  );
}

function OverflowStrip({
  services,
  onOpen,
}: {
  services: Service[];
  onOpen: (systemId: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: OVERFLOW_ID });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "mt-4 rounded-lg border-2 border-dashed border-amber-500/40 p-3",
        isOver && "border-primary bg-primary/5",
      )}
    >
      <div className="mb-2 text-xs text-amber-500">
        {services.length} tile{services.length === 1 ? "" : "s"} don't fit —
        add rows or columns, or drag one into a free cell.
      </div>
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {services.map((svc) => (
          <DraggableTile
            key={svc.system_id}
            svc={svc}
            onClick={() => onOpen(svc.system_id)}
          />
        ))}
      </div>
    </div>
  );
}
